import React from "react";
import { FormDisclaimerFragment } from "types/graphql-types";
import { Trans, useLingui } from "@lingui/react/macro";
import { useGeolocation } from "src/utils/localization/geolocation/GeolocationContext";
import { useLeadForm } from "components/DefaultSubscribeForm/useLeadForm";
import { FormProvider } from "react-hook-form";
import AsyncForm from "components/Form/AsyncForm";
import * as styles from "./RequestBrochureModalForm.module.scss";
import {
  HiddenTrackingFields,
  InputFormAddress1,
  InputFormCity,
  InputFormCountry,
  InputFormEmail,
  InputFormIsNotAgent,
  InputFormNameFirst,
  InputFormNameLast,
  InputFormState,
  InputFormTitle,
  InputFormWorkingWithAgent,
  InputFormZip
} from "components/DefaultSubscribeForm/fields";
import {
  Button,
  FormGenericErrorMessage,
  FormRow,
  InputCheckbox,
  ModalFooter
} from "@silversea-ssc/ui-components-react";
import RequiredFields from "components/DefaultSubscribeForm/RequiredFields";
import DisclaimerMessage from "components/DefaultSubscribeForm/DisclaimerMessage";
import { useCountryState } from "components/DefaultSubscribeForm/DefaultSubscribeForm";
import PhoneInputControl from "components/PhoneInputControl";
import { LeadFormData } from "components/DefaultSubscribeForm/types";
import { LeadAPIRequest } from "src/ssc-api/leadAPIClient";
import { LeadApiResponse } from "@silversea-ssc/dotcom-api-types/Lead";

interface NewRequestBrochureModalFormProps {
  formName: string;
  dataContentName?: string;
  onSubmitSuccess: (
    patchedData: LeadFormData,
    request: LeadAPIRequest,
    response: LeadApiResponse
  ) => void;
  brochureCode: string;
  digitalOnly: boolean;
  formDisclaimerContent: FormDisclaimerFragment | undefined;
}

const RequestBrochureModalForm: React.FC<
  React.PropsWithChildren<NewRequestBrochureModalFormProps>
> = ({
  onSubmitSuccess,
  brochureCode,
  formDisclaimerContent,
  digitalOnly,
  formName,
  dataContentName
}) => {
  const [requestSource, setRequestSource] = React.useState("EBRO");
  const geolocation = useGeolocation();
  const { i18n, t } = useLingui();

  const { formMethods, onSubmit } = useLeadForm({
    askImNotTraveAgent: true,
    askWorkWithAgent: true,
    onSubmitSuccess,
    requestSource,
    requestType: "RAB",
    brochureCode,
    enableSubscribeEmail: true,
    formName
  });

  const { formState, register } = formMethods;
  const { countries, states } = useCountryState(
    !digitalOnly,
    geolocation,
    formMethods
  );

  const switchBrochureRequestSource = (
    val: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRequestSource(val.target.checked ? "BRO" : "EBRO");
  };

  return (
    <FormProvider {...formMethods}>
      <AsyncForm
        name={formName}
        onSubmit={onSubmit}
        className={styles.form}
        dataContentName={dataContentName}
      >
        <HiddenTrackingFields
          register={register}
          brochureCode={!!brochureCode}
          enableSubscribeEmail={true}
          cruiseSuiteInfo={false}
        />
        <div>
          <FormRow columns="2">
            <InputFormTitle register={register} />
          </FormRow>
          <FormRow columns="2">
            <InputFormNameFirst register={register} />
            <InputFormNameLast register={register} />
          </FormRow>
          <InputFormEmail register={register} />

          <React.Suspense fallback={<div></div>}>
            <PhoneInputControl
              name="Phone"
              defaultValue=""
              id="subscribe-modal-phone"
              required={false}
            />
          </React.Suspense>
          <RequiredFields />
        </div>

        {!digitalOnly && (
          <InputCheckbox
            /* eslint-disable-next-line react/jsx-no-bind */
            onChange={switchBrochureRequestSource}
            id="require-phisical-brochure"
            name="SendHardCopy"
          >
            <Trans id="Form SendHardCopy">Also send me a hard copy</Trans>
          </InputCheckbox>
        )}

        {requestSource === "BRO" && (
          <>
            <InputFormAddress1 register={register} />
            <FormRow columns="2">
              <InputFormCountry register={register} countries={countries} />
            </FormRow>
            <InputFormZip register={register} />
            <InputFormCity register={register} />
            <InputFormState register={register} states={states} />
          </>
        )}

        <InputFormIsNotAgent register={register} />
        <div className={styles.extraFieldsContainer}>
          <InputFormWorkingWithAgent register={register} />
        </div>

        <div className={styles.disclaimerText}>
          <DisclaimerMessage content={formDisclaimerContent} />
        </div>

        <ModalFooter>
          {!formState.isValid && formState.isSubmitted && (
            <FormGenericErrorMessage message="One or more fields have an error" />
          )}
          <Button
            className={styles.bodyFooterButton}
            renderAs="button"
            type="submit"
            loading={formState.isSubmitting}
            //disabled={formState.isSubmitting}
            data-testid="submit"
            dataContentPiece="submit_button"
            variant="accent"
            noWrap
          >
            {i18n._(t`SUBMIT`)}
          </Button>
        </ModalFooter>
      </AsyncForm>
    </FormProvider>
  );
};

export default RequestBrochureModalForm;
